/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CELL_NO_OVERFLOW_CLASS,
  CELL_TEXT_CENTER_CLASS,
  CELL_TEXT_RIGHT_CLASS,
  code3Formatter,
  defaultCurrencyCellRendererDecorator,
  outerLabelCellRendererDecorator,
  ROW_FOOTER_LIGHT_CLASS,
} from '@gh/common-internal-shared';
import { getRouteInitData, NumberTypes } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  buildNumberColumn,
  cellRendererSelectorForPinnedRow,
  GridDefinition,
  GridDefinitionFactory,
  typedValueFormatter,
} from '@gh/core-ui-grid';
import { sumBy } from 'lodash';
import {
  calculateLendersScheduleTotals,
  JobDrawsLenderScheduleItemPlacement,
  JobDrawsLendersScheduleItem,
} from '../../../resources/job-details-tab';

@Component({
  standalone: false,
  selector: 'gh-job-details-draws-lenders-schedule-subtab',
  templateUrl: 'job-details-draws-lenders-schedule-subtab.component.html',
  styleUrls: ['job-details-draws-lenders-schedule-subtab.component.scss'],
})
export class JobDetailsDrawsLendersScheduleSubtabComponent implements OnInit, OnChanges {
  @Input() editable: boolean;
  @Input() schedule: JobDrawsLendersScheduleItem[];
  @Input() contractAmount: number;
  @Input() contractAmountLenders: number;

  @Output() printSchedule = new EventEmitter<void>();
  @Output() refreshSchedule = new EventEmitter<void>();

  includedGridDefinition: GridDefinition;
  overheadsGridDefinition: GridDefinition;

  included: JobDrawsLendersScheduleItem[];
  overheads: JobDrawsLendersScheduleItem[];

  includedTotals: JobDrawsLendersScheduleItem[];
  overheadTotals: JobDrawsLendersScheduleItem[];

  totalOrderCost: number;
  totalOrderCostPct: number;
  profit: number;

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    this.includedGridDefinition = this.buildIncludedGridDefinition();
    this.overheadsGridDefinition = this.buildOverheadsGridDefinition();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.prototype.hasOwnProperty.call(changes, 'schedule')) {
      const { schedule } = this;

      const totalOrderCost = sumBy(schedule, 'orderCost');

      this.included = schedule.filter((item) => !item.isExcluded);
      this.overheads = schedule.filter((item) => item.isExcluded);

      this.includedTotals = calculateLendersScheduleTotals(this.included, totalOrderCost);
      this.overheadTotals = calculateLendersScheduleTotals(this.overheads, totalOrderCost);

      this.totalOrderCost = totalOrderCost;
      this.totalOrderCostPct = totalOrderCost ? sumBy(schedule, 'orderCost') / totalOrderCost * 100 : 0;
      this.profit = this.contractAmount - totalOrderCost;
    }
  }

  private buildIncludedGridDefinition(): GridDefinition {
    const { lendersScheduleIncludedGridLayout } = getRouteInitData(this.route);

    const pinnedPriceRenderer = defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.SignedPrice));
    const gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        code: { valueFormatter: code3Formatter, minWidth: 100 },
        orderNumber: { cellClass: CELL_TEXT_CENTER_CLASS, minWidth: 100 },
        description: { minWidth: 200 },
        orderCost: {
          ...buildNumberColumn(NumberTypes.SignedPrice),
          cellClass: [CELL_NO_OVERFLOW_CLASS, CELL_TEXT_RIGHT_CLASS],
          cellRendererSelector: cellRendererSelectorForPinnedRow(
            outerLabelCellRendererDecorator(
              this.translations.get('lbl_column_total'),
              20)(pinnedPriceRenderer)),
        },
        orderCostPct: buildNumberColumn(NumberTypes.Percents_2),
        amountDrawn: {
          ...buildNumberColumn(NumberTypes.SignedPrice),
          cellRendererSelector: cellRendererSelectorForPinnedRow(pinnedPriceRenderer),
        },
        balance: {
          ...buildNumberColumn(NumberTypes.SignedPrice),
          cellRendererSelector: cellRendererSelectorForPinnedRow(pinnedPriceRenderer),
        },
      },
      {
        noAutomation: true,
        rowClassRules: {
          [ROW_FOOTER_LIGHT_CLASS]: ({ data }: { data: JobDrawsLendersScheduleItem }) =>
            data.placement === JobDrawsLenderScheduleItemPlacement.Total,
        },
      });
    gridDefinition.bind(lendersScheduleIncludedGridLayout);
    return gridDefinition;
  }

  private buildOverheadsGridDefinition(): GridDefinition {
    const { lendersScheduleOverheadsGridLayout } = getRouteInitData(this.route);

    const pinnedPriceRenderer = defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.SignedPrice));
    const gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        code: { valueFormatter: code3Formatter, minWidth: 100 },
        orderNumber: { cellClass: CELL_TEXT_CENTER_CLASS, minWidth: 100 },
        description: { minWidth: 200 },
        orderCost: {
          ...buildNumberColumn(NumberTypes.SignedPrice),
          cellClass: [CELL_NO_OVERFLOW_CLASS, CELL_TEXT_RIGHT_CLASS],
          cellRendererSelector: cellRendererSelectorForPinnedRow(
            outerLabelCellRendererDecorator(
              this.translations.get('lbl_column_total'),
              20)(pinnedPriceRenderer)),
        },
        orderCostPct: buildNumberColumn(NumberTypes.Percents_2),
      },
      {
        noAutomation: true,
        rowClassRules: {
          [ROW_FOOTER_LIGHT_CLASS]: ({ data }: { data: JobDrawsLendersScheduleItem }) =>
            data.placement === JobDrawsLenderScheduleItemPlacement.Total,
        },
      });
    gridDefinition.bind(lendersScheduleOverheadsGridLayout);
    return gridDefinition;
  }
}
